/**
 * Pure order-book view model for one outcome side (#E0 or #E1).
 *
 * HL `l2Book` levels are [bids (best first, descending), asks (best first, ascending)].
 * The view shows asks above bids (trade.xyz / outcome.xyz layout): asks in descending price so the best
 * ask sits right above the spread line, bids below it in descending price. Cumulative size grows away
 * from the spread on both sides; depth bars are cumulative size relative to the deeper side's total.
 */
import type { L2Book, L2Level } from '../hl/types';
import { pxToUnits } from './format';

/** Price levels shown per side of the book (#2242: a hard cap, and the book keeps the height of this many rows). */
export const BOOK_LEVELS = 6;

export interface BookRow {
  /** Raw wire price (tooltip, ticket prefill). */
  px: string;
  /** Price in 1e-5 units. */
  units: number;
  sz: number;
  n: number;
  /** Cumulative size from the best price out to (and including) this level. */
  cum: number;
  /** cum / maxCum, 0..1, for the depth bar. */
  depth: number;
}

export interface BookView {
  /** Display order: top to bottom (highest ask first, best ask last). */
  asks: BookRow[];
  /** Display order: top to bottom (best bid first). */
  bids: BookRow[];
  bestBid: BookRow | null;
  bestAsk: BookRow | null;
  /** Units (1e-5); null unless both sides have a level. */
  spread: number | null;
  /** Units (1e-5, may be x.5); null unless both sides have a level. */
  mid: number | null;
  maxCum: number;
  empty: boolean;
}

function accumulate(levels: readonly L2Level[], maxLevels: number): Omit<BookRow, 'depth'>[] {
  let cum = 0;
  const out: Omit<BookRow, 'depth'>[] = [];
  for (const l of levels.slice(0, maxLevels)) {
    const sz = Number(l.sz);
    if (!Number.isFinite(sz)) continue;
    cum += sz;
    out.push({ px: l.px, units: pxToUnits(l.px), sz, n: l.n, cum });
  }
  return out;
}

export function buildBookView(book: Pick<L2Book, 'levels'> | null | undefined, maxLevels = BOOK_LEVELS): BookView {
  const bidLevels = book?.levels?.[0] ?? [];
  const askLevels = book?.levels?.[1] ?? [];
  const bidsRaw = accumulate(bidLevels, maxLevels);
  const asksRaw = accumulate(askLevels, maxLevels);
  const maxCum = Math.max(bidsRaw.at(-1)?.cum ?? 0, asksRaw.at(-1)?.cum ?? 0);
  const withDepth = (r: Omit<BookRow, 'depth'>): BookRow => ({ ...r, depth: maxCum > 0 ? r.cum / maxCum : 0 });
  const bids = bidsRaw.map(withDepth);
  const asksBestFirst = asksRaw.map(withDepth);
  const bestBid = bids[0] ?? null;
  const bestAsk = asksBestFirst[0] ?? null;
  const both = bestBid !== null && bestAsk !== null;
  return {
    asks: asksBestFirst.slice().reverse(),
    bids,
    bestBid,
    bestAsk,
    spread: both ? bestAsk.units - bestBid.units : null,
    mid: both ? (bestAsk.units + bestBid.units) / 2 : null,
    maxCum,
    empty: bids.length === 0 && asksBestFirst.length === 0,
  };
}
